import React, { useEffect, useState, useCallback } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Grid, Typography, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField } from "@mui/material";
import { fetchEmployeeById } from "../../../services/Employee/EmployeeApi";
import SalaryPayment from "./SalaryPayment";
// إضافة استيراد المكون الجديد
import AddDiscountEmployee from "./PayTOEmployee";
// Component props:
// - open: boolean
// - employeeId: id of employee to load
// - onClose(): close dialog
const EmployeeAccountStatement = ({ open, employeeId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [error, setError] = useState(null);

  // Add state to control SalaryPayment dialog
  const [openSalaryPayment, setOpenSalaryPayment] = useState(false);

  // جديد: حالة فتح/إغلاق مكون الخصم
  const [openAddDiscount, setOpenAddDiscount] = useState(false);

  // جديد: حالة الفلتر بالتاريخ
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // جديد: حالة البحث
  const [searchQuery, setSearchQuery] = useState("");

  // extractable loader so we can call it from event handler too
  const loadEmployee = useCallback(async (id) => {
    if (!id) {
      setEmployee(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchEmployeeById(id);
      // fetchEmployeeById may return array or object; normalize to single employee object
      const emp = Array.isArray(res) ? res[0] : (res?.data && Array.isArray(res.data) ? res.data[0] : res);
      setEmployee(emp || null);
    } catch (err) {
      setError(err?.message || "فشل في جلب بيانات الموظف");
    } finally {
      setLoading(false);
    }
  }, []);

  // initial load when dialog opens or employeeId changes
  useEffect(() => {
    if (open && employeeId) {
      loadEmployee(employeeId);
    } else if (!open) {
      setEmployee(null);
      setError(null);
    }
  }, [open, employeeId, loadEmployee]);

  // listen for global event to refresh statements
  useEffect(() => {
    const handler = (e) => {
      const id = e?.detail?.employeeId ?? null;
      // only refresh if dialog open and ids match (or no id provided fall back to current)
      const targetId = id || employeeId;
      if (open && targetId && targetId === employeeId) {
        loadEmployee(targetId);
      }
    };
    window.addEventListener("employeeAccountStatementUpdated", handler);
    return () => window.removeEventListener("employeeAccountStatementUpdated", handler);
  }, [open, employeeId, loadEmployee]);

  const formatDate = (val) => {
    if (!val) return "-";
    try {
      const d = new Date(val);
      if (!isNaN(d.getTime())) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      }
      const s = String(val);
      return s.length >= 10 ? s.slice(0, 10) : s;
    } catch {
      return String(val).slice(0, 10);
    }
  };

  const getSalaryToDisplay = (emp) => {
    if (!emp) return "-";
    const type = emp.salary_type || "monthly";
    const key = `${type}_salary`;
    return emp[key] ?? emp.monthly_salary ?? emp.weekly_salary ?? emp.daily_salary ?? "-";
  };

  // NEW: خريطة تسميات أنواع الراتب بالعربي
  const SALARY_TYPE_LABELS = {
    monthly: "شهري",
    daily: "يومي",
    weekly: "أسبوعي",
  };
  const getSalaryTypeLabel = (type) => {
    if (!type) return "-";
    return SALARY_TYPE_LABELS[type] ?? type;
  };

  // جديد: خريطة وتابع لإرجاع اسم النوع بالعربي
  const ACTION_TYPE_LABELS = {
    payment: "سداد",
    advance: "سلفة",
    payroll: "مرتب",
    discount: "خصم",
    // أضف أنواع أخرى هنا عند الحاجة
  };
  const actionTypeLabel = (type) => {
    if (!type) return "-";
    // إذا كانت القيمة نصًا قابلًا للعرض مباشرة (مثلاً بالعربي) أرجعها
    return ACTION_TYPE_LABELS[type] ?? type ?? "-";
  };

  // NEW: export current employee + statements to CSV (Excel-friendly)
  const exportEmployeeToCSV = () => {
    if (!employee) {
      alert("لا توجد بيانات للتصدير");
      return;
    }
    // employee meta row
    const metaHeaders = ["حقل", "قيمة"];
    const metaRows = [
      ["اسم", employee.name ?? "-"],
      ["دور", employee.role ?? "-"],
      ["البريد", employee.email ?? "-"],
      ["الهاتف", employee.phone ?? "-"],
      ["نوع الراتب", getSalaryTypeLabel(employee.salary_type)],
      ["الراتب", getSalaryToDisplay(employee)],
      ["الحالة", employee.active ? "نشط" : "غير نشط"],
    ];

    // statements headers
    const stmtHeaders = ["التاريخ", "النوع", "المبلغ", "البونص", "الخصم", "المدفوع", "الخزنة/البنك", "المستخدم", "ملاحظة", "ملف"];
    const stmtRows = Array.isArray(employee.statements) ? employee.statements.map((s) => [
      s.date ? formatDate(s.date) : formatDate(s.created_at),
      actionTypeLabel(s.action_type),
      s.orginal_amount ?? "",
      s.bouns ?? "",
      s.discount ?? "",
      s.total ?? "",
      s.bank?.name ?? s.bank_id ?? "",
      s.user?.name ?? s.user_id ?? "",
      s.note ?? "",
      s.file ?? "",
    ]) : [];

    // build CSV content: meta section, blank line, statements section
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [];
    lines.push(metaHeaders.map(escape).join(","));
    metaRows.forEach(r => lines.push(r.map(escape).join(",")));
    lines.push(""); // blank
    lines.push(stmtHeaders.map(escape).join(","));
    stmtRows.forEach(r => lines.push(r.map(escape).join(",")));

    const csv = lines.join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${employee.name ? employee.name.replace(/\s+/g,'_') : "employee"}_statements.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // جديد: دالة لتصفية العمليات المالية حسب التاريخ والبحث
  const getFilteredStatements = useCallback(() => {
    if (!employee?.statements || !Array.isArray(employee.statements)) {
      return [];
    }

    let filtered = [...employee.statements];

    // فلتر التاريخ
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter((s) => {
        const stmtDate = new Date(s.date || s.created_at);
        return stmtDate >= fromDate;
      });
    }

    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter((s) => {
        const stmtDate = new Date(s.date || s.created_at);
        return stmtDate <= toDate;
      });
    }

    // فلتر البحث
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((s) => {
        const searchFields = [
          actionTypeLabel(s.action_type),
          s.orginal_amount?.toString(),
          s.bouns?.toString(),
          s.discount?.toString(),
          s.total?.toString(),
          s.bank?.name,
          s.user?.name,
          s.note,
        ];
        
        return searchFields.some(field => 
          field?.toString().toLowerCase().includes(query)
        );
      });
    }

    return filtered;
  }, [employee, dateFrom, dateTo, searchQuery]);

  // جديد: دالة لإعادة تعيين الفلتر والبحث
  const resetFilter = () => {
    setDateFrom("");
    setDateTo("");
    setSearchQuery("");
  };

  // جديد: دالة لحساب إجمالي المدفوع
  const calculateTotalPaid = useCallback((statements) => {
    if (!statements || statements.length === 0) return 0;
    return statements.reduce((sum, s) => {
      const total = parseFloat(s.total) || 0;
      return sum + total;
    }, 0);
  }, []);

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: "92vh",     // زيادة الارتفاع
          maxHeight: "98vh",
        },
      }}
    >
      {/* نقل الأزرار للأعلى مع العنوان */}
      <DialogTitle sx={{ py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" component="span">كشف حساب الموظف</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button 
            variant="contained" 
            color="success"
            onClick={exportEmployeeToCSV} 
            disabled={!employee}
            size="medium"
            sx={{ 
              minWidth: 120,
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4
              }
            }}
          >
            تصدير إلى Excel
          </Button>
          <Button 
            variant="contained"
            color="error" 
            onClick={() => setOpenAddDiscount(true)} 
            disabled={!employeeId}
            size="medium"
            sx={{ 
              minWidth: 100,
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4
              }
            }}
          >
            سداد
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress /></Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : !employee ? (
          <Typography sx={{ py: 2 }}>لا توجد بيانات لعرضها.</Typography>
        ) : (
          <>
            {/* Top: employee basic info - تقليل المساحة */}
            <Box sx={{ mb: 1.5, p: 1.5, bgcolor: "background.paper", borderRadius: 1, border: "1px solid #e0e0e0" }}>
              <Grid container spacing={1}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">الاسم</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{employee.name ?? "-"}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">الهاتف</Typography>
                  <Typography variant="body2">{employee.phone ?? "-"}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">الدور</Typography>
                  <Typography variant="body2">{employee.role ?? "-"}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">الراتب ({getSalaryTypeLabel(employee.salary_type)})</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{getSalaryToDisplay(employee)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">الفرع</Typography>
                  <Typography variant="body2">{employee.root?.name ?? (employee.root_id ?? "-")}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">الحالة</Typography>
                  <Typography variant="body2">{employee.active ? "نشط" : "غير نشط"}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: "bold" }}>المتبقي على الموظف</Typography>
                  <Typography 
                    variant="body2"
                    sx={{ 
                      fontWeight: "bold", 
                      fontSize: "1rem",
                      color: employee.remaining_salary > 0 ? "error.main" : "success.main"
                    }}
                  >
                    {employee.remaining_salary ?? 0}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* جديد: شريط البحث والفلتر - تقليل المساحة */}
            <Box sx={{ mb: 1.5, p: 1.5, bgcolor: "background.paper", borderRadius: 1, border: "1px solid #e0e0e0" }}>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} sm={12}>
                  <TextField
                    label="بحث في العمليات"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="النوع، المبلغ، الملاحظة، المستخدم..."
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="من تاريخ"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="إلى تاريخ"
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button 
                    variant="outlined" 
                    onClick={resetFilter}
                    disabled={!dateFrom && !dateTo && !searchQuery}
                    fullWidth
                    size="small"
                  >
                    إعادة تعيين
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {/* Bottom: financial statements - مساحة أكبر */}
            <Box sx={{ flexGrow: 1, overflow: "auto" }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                العمليات المالية
                {(dateFrom || dateTo || searchQuery) && (
                  <Typography component="span" variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                    ({getFilteredStatements().length} من {employee.statements?.length ?? 0})
                  </Typography>
                )}
              </Typography>
              {(() => {
                // هنا يتم حساب البيانات المصفاة والإجمالي معاً
                const filteredStatements = getFilteredStatements();
                const totalPaid = calculateTotalPaid(filteredStatements);
                
                return filteredStatements.length > 0 ? (
                  <TableContainer component={Paper} sx={{ maxHeight: "calc(85vh - 280px)" }}>
                    <Table size="medium" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.95rem" }}>التاريخ</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.95rem" }}>النوع</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.95rem" }}>المدفوع</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.95rem" }}>الخزنة / البنك</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.95rem" }}>المستخدم</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.95rem" }}>ملاحظة</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.95rem" }}>ملف</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredStatements.map((s) => (
                          <TableRow key={s.id} hover>
                            <TableCell sx={{ fontSize: "0.9rem" }}>
                              {s.date ? formatDate(s.date) : formatDate(s.created_at)}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.9rem", fontWeight: 500 }}>
                              {actionTypeLabel(s.action_type)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
                              {s.total ?? "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.9rem" }}>
                              {s.bank?.name ?? s.bank_id ?? "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.9rem" }}>
                              {s.user?.name ?? s.user_id ?? "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.9rem" }}>
                              {s.note ?? "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.9rem" }}>
                              {s.file ? (
                                <a href={s.file} target="_blank" rel="noreferrer" style={{ fontWeight: 500 }}>
                                  فتح
                                </a>
                              ) : "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* صف الإجمالي - يتم حسابه من filteredStatements */}
                        <TableRow sx={{ bgcolor: "action.hover", position: "sticky", bottom: 0 }}>
                          <TableCell colSpan={2} align="right">
                            <Typography variant="body1" sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                              الإجمالي{(dateFrom || dateTo || searchQuery) ? " (المصفى)" : ""}:
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body1" sx={{ fontWeight: "bold", color: "primary.main", fontSize: "1.05rem" }}>
                              {totalPaid.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell colSpan={4}></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2">
                    {(dateFrom || dateTo || searchQuery) ? "لا توجد عمليات مالية مطابقة للبحث أو الفترة المحددة." : "لا توجد عمليات مالية مسجلة."}
                  </Typography>
                );
              })()}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ py: 1 }}>
        <Button onClick={onClose} size="small">إغلاق</Button>
      </DialogActions>

      {/* رندر المكوّن الجديد */}
      <AddDiscountEmployee
        open={openAddDiscount}
        employeeId={employeeId}
        onClose={() => setOpenAddDiscount(false)}
        onSuccess={() => {
          setOpenAddDiscount(false);
          // أخبر باقي الواجهة بالتحديث
          window.dispatchEvent(new CustomEvent("employeeAccountStatementUpdated", { detail: { employeeId } }));
        }}
      />

      {/* Render SalaryPayment and pass employeeId */}
      <SalaryPayment
        open={openSalaryPayment}
        employeeId={employeeId}
        onClose={() => setOpenSalaryPayment(false)}
      />
    </Dialog>
  );
};

export default EmployeeAccountStatement;